import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'INR'];
const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney'
];

export default function ProfileTab({ onToast }) {
  const { user, userProfile, updateProfile } = useAuth();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: '',
    currency_preference: 'USD',
    timezone: 'UTC',
  });

  useEffect(() => {
    if (userProfile) {
      setForm({
        first_name: userProfile?.first_name || '',
        last_name: userProfile?.last_name || '',
        phone: userProfile?.phone || '',
        date_of_birth: userProfile?.date_of_birth || '',
        currency_preference: userProfile?.currency_preference || 'USD',
        timezone: userProfile?.timezone || 'UTC',
      });
      if (userProfile?.avatar_url) {
        loadAvatarUrl(userProfile?.avatar_url);
      }
    }
  }, [userProfile]);

  const loadAvatarUrl = async (path) => {
    if (!path) return;
    try {
      const { data, error } = await supabase?.storage?.from('avatars')?.createSignedUrl(path, 3600);
      if (!error && data?.signedUrl) setAvatarUrl(data?.signedUrl);
    } catch (e) {
      console.error('Avatar load error:', e);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleAvatarClick = () => fileInputRef?.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const ext = file?.name?.split('.')?.pop();
      const filePath = `${user?.id}/avatar-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase?.storage?.from('avatars')?.upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { error: updateError } = await updateProfile({ avatar_url: filePath });
      if (updateError) throw updateError;
      await loadAvatarUrl(filePath);
      onToast?.({ type: 'success', message: 'Avatar updated successfully!' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Failed to upload avatar' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await updateProfile(form);
      if (error) throw error;
      setHasChanges(false);
      onToast?.({ type: 'success', message: 'Profile saved successfully!' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const initials = `${form?.first_name?.[0] || ''}${form?.last_name?.[0] || ''}`?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className="space-y-8">
      {/* Avatar Section */}
      <div className="flex flex-col sm:flex-row items-center gap-6 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,229,255,0.12)' }}>
        <div className="relative group">
          <div
            onClick={handleAvatarClick}
            className="w-24 h-24 rounded-full flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-300 group-hover:ring-2 group-hover:ring-[#00E5FF]"
            style={{ background: 'linear-gradient(135deg, #00E5FF22, #7B2FBE22)', border: '2px solid rgba(0,229,255,0.3)' }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold" style={{ color: '#00E5FF' }}>{initials}</span>
            )}
            {uploading && (
              <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{ background: 'rgba(13,27,42,0.7)' }}>
                <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110"
            style={{ background: '#00E5FF', boxShadow: '0 0 12px rgba(0,229,255,0.5)' }}
            onClick={handleAvatarClick}
          >
            <Icon name="Camera" size={14} color="#0D1B2A" />
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">{form?.first_name || form?.last_name ? `${form?.first_name} ${form?.last_name}`?.trim() : 'Your Name'}</h3>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>{user?.email}</p>
          <p className="text-xs mt-2" style={{ color: '#64748B' }}>Click avatar to upload. Max 2MB. JPG, PNG, WebP.</p>
        </div>
      </div>
      {/* Form Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {[
          { label: 'First Name', field: 'first_name', type: 'text', placeholder: 'John' },
          { label: 'Last Name', field: 'last_name', type: 'text', placeholder: 'Doe' },
          { label: 'Phone Number', field: 'phone', type: 'tel', placeholder: '+1 (555) 000-0000' },
          { label: 'Date of Birth', field: 'date_of_birth', type: 'date', placeholder: '' },
        ]?.map(({ label, field, type, placeholder }) => (
          <div key={field}>
            <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>{label}</label>
            <input
              type={type}
              value={form?.[field] || ''}
              placeholder={placeholder}
              onChange={e => handleChange(field, e?.target?.value)}
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-[#475569] outline-none transition-all duration-200 focus:ring-2 focus:ring-[#00E5FF]"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.15)' }}
            />
          </div>
        ))}

        {/* Email (read-only) */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>Email Address</label>
          <div className="relative">
            <input
              type="email"
              value={user?.email || ''}
              readOnly
              className="w-full px-4 py-3 rounded-xl text-sm outline-none cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', color: '#64748B' }}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Icon name="Lock" size={14} color="#475569" />
            </div>
          </div>
          <p className="text-xs mt-1" style={{ color: '#475569' }}>Email is managed by your auth provider</p>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>Currency Preference</label>
          <select
            value={form?.currency_preference || 'USD'}
            onChange={e => handleChange('currency_preference', e?.target?.value)}
            className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200 focus:ring-2 focus:ring-[#00E5FF]"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.15)' }}
          >
            {CURRENCIES?.map(c => <option key={c} value={c} style={{ background: '#0D1B2A' }}>{c}</option>)}
          </select>
        </div>

        {/* Timezone */}
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>Timezone</label>
          <select
            value={form?.timezone || 'UTC'}
            onChange={e => handleChange('timezone', e?.target?.value)}
            className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200 focus:ring-2 focus:ring-[#00E5FF]"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.15)' }}
          >
            {TIMEZONES?.map(tz => <option key={tz} value={tz} style={{ background: '#0D1B2A' }}>{tz}</option>)}
          </select>
        </div>
      </div>
      {/* Save Button */}
      <div className="flex items-center justify-between pt-2">
        {hasChanges && (
          <p className="text-xs flex items-center gap-1.5" style={{ color: '#F59E0B' }}>
            <Icon name="AlertCircle" size={13} color="#F59E0B" />
            You have unsaved changes
          </p>
        )}
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="ml-auto flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02]"
          style={{ background: hasChanges ? 'linear-gradient(135deg, #00E5FF, #0891B2)' : 'rgba(255,255,255,0.05)', color: hasChanges ? '#0D1B2A' : '#475569', boxShadow: hasChanges ? '0 0 20px rgba(0,229,255,0.3)' : 'none' }}
        >
          {saving ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={15} color="currentColor" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
